// v1 functional domain picker — upgraded from v0 hint-card per
// MARKETPLACE tick-7 REQUEST. Renders only when IntegrationConfigureForm
// detects `_uiFlow === 'registrar'` (guard in the form's slot).
//
// UI:
//   - SearchRow: input + button → calls `domain.search` capability.
//   - ResultsList: maps `searchResults` → MarketplaceRegistrarSearchResult rows.
//   - OwnedDomains: list from `domain.list` capability, fired on mount.
//
// State is local to this component's subtree (not root) — smbls v3.14
// `state: {}` scopes state inheritance so two pickers on the same page
// don't share searchResults / ownedDomains.
//
// Wiring:
//   - `callCapabilityAndSet(inflightKey, resultKey, errorKey, capability, args, state)`
//     is a helper in `workspace/packages/marketplace/functions/index.js`
//     (shipped by MARKETPLACE per tick-7). Stubs throw until wired; same
//     pattern as `startOAuthFlow`.
//   - The search + register flow hits `integration-call` edge fn (v13+)
//     with `kind = <registrar kind>` + `capability ∈ ['domain.search',
//     'domain.register', 'domain.list']` → routed via
//     `integration_kinds.registrar_provider` to the right module in
//     `_shared/registrars/` (today: porkbun.ts).

export const MarketplaceRegistrarDomainPicker = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  borderRadius: 'Z',
  border: '1px solid line',
  background: 'layer',
  marginTop: 'Z',

  state: {
    searchQuery: '',
    searching: false,
    searchResults: null,
    searchError: null,
    ownedDomains: null,
    registering: false,
    registerResult: null,
    registerError: null
  },

  // Fire `domain.list` on mount to populate the "Your domains" section.
  // Only runs when there's an API key configured (i.e. the integration
  // has been saved at least once) — the form state's `apiKey` field
  // signals that.
  onCreate: (el, s) => {
    if (s?.apiKey) {
      el.call('callCapabilityAndSet', 'listing', 'ownedDomains', 'listError', 'domain.list', {}, s)
    }
  },

  Title: {
    tag: 'h5',
    text: 'Domain management',
    fontSize: 'Z',
    fontWeight: '600',
    color: 'title',
    margin: '0'
  },

  Hint: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y',
    color: 'caption',
    // Hidden once an API key is configured.
    hide: (el, s) => !!s?.apiKey,
    text: 'Enter your API key above to enable domain search.'
  },

  SearchRow: {
    flow: 'x',
    gap: 'Z',
    align: 'center',
    hide: (el, s) => !s?.apiKey,

    SearchInput: {
      extends: 'Input',
      flex: '1',
      placeholder: 'example.com',
      value: (el, s) => s.searchQuery || '',
      onInput: (e, el, s) => s.update({ searchQuery: e.target.value })
    },

    SearchBtn: {
      extends: 'Button',
      theme: 'primary',
      disabled: (el, s) => !s.searchQuery || !!s.searching,
      text: (el, s) => s.searching ? 'Searching…' : 'Search',
      onClick: (e, el, s) => el.call(
        'callCapabilityAndSet',
        'searching',
        'searchResults',
        'searchError',
        'domain.search',
        { query: s.searchQuery },
        s
      )
    }
  },

  SearchError: {
    hide: (el, s) => !s?.searchError,
    tag: 'p',
    fontSize: 'Y',
    color: 'danger',
    margin: '0',
    text: (el, s) => s.searchError || ''
  },

  ResultsList: {
    flow: 'y',
    gap: 'X',
    hide: (el, s) => !(Array.isArray(s?.searchResults) && s.searchResults.length),
    childExtends: 'MarketplaceRegistrarSearchResult',
    children: (el, s) => s.searchResults || []
  },

  EmptyResults: {
    hide: (el, s) => !(Array.isArray(s?.searchResults) && s.searchResults.length === 0),
    tag: 'p',
    fontSize: 'Y',
    color: 'caption',
    margin: '0',
    text: 'No results. Try a different query.'
  },

  OwnedDomainsSection: {
    flow: 'y',
    gap: 'X',
    marginTop: 'Z',
    hide: (el, s) => !(Array.isArray(s?.ownedDomains) && s.ownedDomains.length),

    OwnedTitle: {
      tag: 'h6',
      fontSize: 'Z',
      fontWeight: '600',
      color: 'title',
      margin: '0',
      text: 'Your domains'
    },

    OwnedList: {
      flow: 'y',
      gap: 'X',
      // Same mini-row component used for search results — it handles
      // the domain name + (hidden) price + (hidden) register button
      // gracefully. Here the rows surface expiration info via a simple
      // text mapping before the row.
      children: (el, s) => (s.ownedDomains || []).map((d) => ({
        flow: 'x',
        align: 'center space-between',
        gap: 'Z',
        padding: 'Y Z',
        borderRadius: 'Y',
        border: '1px solid line',
        background: 'document',
        Domain: {
          tag: 'span',
          flex: '1',
          fontSize: 'Z2',
          fontWeight: '500',
          color: 'title',
          text: d.domain || ''
        },
        ExpiresAt: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: d.expiresAt ? `renews ${String(d.expiresAt).slice(0, 10)}` : ''
        }
      }))
    }
  }
}
